import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import { Agentation } from 'agentation';
import './App.css';

import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:8080' : '';

function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    var r = Math.random() * 16 | 0, v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
}

const TYPE_CONFIG = {
  EV01: { label: '\uCD95\uC81C', emoji: '\uD83C\uDF89', color: '#E85D2F', light: '#FFF0EB' },
  EV02: { label: '\uACF5\uC5F0', emoji: '\uD83C\uDFAD', color: '#7B3FE4', light: '#F3EEFF' },
  EV03: { label: '\uD589\uC0AC', emoji: '\uD83C\uDFAA', color: '#0D8A6A', light: '#E6F7F3' },
  STD:  { label: '\uD45C\uC900', emoji: '\uD83D\uDCCB', color: '#374151', light: '#F3F4F6' },
};
const getTC = (type) => TYPE_CONFIG[type] || { label: type || '\uAE30\uD0C0', emoji: '\uD83D\uDCCD', color: '#374151', light: '#F3F4F6' };

const REGIONS = [
  '\uC11C\uC6B8', '\uBD80\uC0B0', '\uB300\uAD6C', '\uC778\uCC9C', '\uAD11\uC8FC', '\uB300\uC804', '\uC6B8\uC0B0', '\uC138\uC885',
  '\uACBD\uAE30', '\uAC15\uC6D0', '\uCDA9\uBD81', '\uCDA9\uB0A8', '\uC804\uBD81', '\uC804\uB0A8', '\uACBD\uBD81', '\uACBD\uB0A8', '\uC81C\uC8FC',
];

const REGION_CENTERS = {
  '\uC11C\uC6B8': [37.5665, 126.9780],
  '\uBD80\uC0B0': [35.1796, 129.0756],
  '\uB300\uAD6C': [35.8714, 128.6014],
  '\uC778\uCC9C': [37.4563, 126.7052],
  '\uAD11\uC8FC': [35.1595, 126.8526],
  '\uB300\uC804': [36.3504, 127.3845],
  '\uC6B8\uC0B0': [35.5389, 129.3114],
  '\uC138\uC885': [36.4801, 127.2890],
  '\uACBD\uAE30': [37.4138, 127.5183],
  '\uAC15\uC6D0': [37.8228, 128.1555],
  '\uCDA9\uBD81': [36.6356, 127.8727],
  '\uCDA9\uB0A8': [36.5184, 126.8000],
  '\uC804\uBD81': [35.7175, 127.1530],
  '\uC804\uB0A8': [34.8679, 126.9910],
  '\uACBD\uBD81': [36.4919, 128.8889],
  '\uACBD\uB0A8': [35.4606, 128.2132],
  '\uC81C\uC8FC': [33.4996, 126.5312],
};

function getFestivalRegion(f) {
  const addr = f.addr1 || '';
  for (const r of REGIONS) {
    if (addr.startsWith(r)) return r;
  }
  for (const r of REGIONS) {
    if (addr.includes(r)) return r;
  }
  return null;
}

function getRepresentativeLocalName(item) {
  if (item.addr1) {
    const cleanAddr = item.addr1.trim();
    const parts = cleanAddr.split(/\s+/);
    if (parts.length > 0) {
      if (parts.length > 1) {
        let local = parts[1].replace(/(시|군|구)$/, '');
        if (local.length >= 2 && local.length <= 4) {
          return local;
        }
      }
      let firstLocal = parts[0].replace(/(시|군|구)$/, '');
      const isProvince = /^(경기|서울|인천|부산|대구|대전|광주|ulsan|울산|세종|강원|충북|충남|전북|전남|경북|경남|제주)(도|시)?$/.test(firstLocal);
      if (!isProvince && firstLocal.length >= 2 && firstLocal.length <= 4) {
        return firstLocal;
      }
    }
  }
  if (item.region) {
    let reg = item.region.trim()
      .replace(/(시|도)$/, '')
      .replace(/특별(자치)?/, '');
    if (reg === '충청북도') reg = '충북';
    if (reg === '충청남도') reg = '충남';
    if (reg === '전라북도') reg = '전북';
    if (reg === '전라남도') reg = '전남';
    if (reg === '경상북도') reg = '경북';
    if (reg === '경상남도') reg = '경남';
    if (reg.length >= 2) return reg;
  }
  return item.eventType === 'TOUR' ? '관광' : '행사';
}

function getGridClusters(items, zoom, mapBounds) {
  let dLat = 0.8;
  let dLng = 0.8;

  if (zoom <= 7) {
    dLat = 0.8; dLng = 0.8;
  } else if (zoom === 8) {
    dLat = 0.45; dLng = 0.45;
  } else if (zoom === 9) {
    dLat = 0.22; dLng = 0.22;
  } else if (zoom === 10) {
    dLat = 0.12; dLng = 0.12;
  } else if (zoom === 11) {
    dLat = 0.06; dLng = 0.06;
  } else if (zoom === 12) {
    dLat = 0.03; dLng = 0.03;
  } else if (zoom === 13) {
    dLat = 0.015; dLng = 0.015;
  } else if (zoom === 14) {
    dLat = 0.007; dLng = 0.007;
  } else {
    dLat = 0.003; dLng = 0.003;
  }

  const clusters = {};

  items.forEach(item => {
    const lat = parseFloat(item.mapy);
    const lng = parseFloat(item.mapx);
    if (isNaN(lat) || isNaN(lng)) return;

    if (mapBounds && !mapBounds.contains([lat, lng])) return;

    const gridX = Math.floor(lng / dLng);
    const gridY = Math.floor(lat / dLat);
    const key = `${gridX}_${gridY}`;

    if (!clusters[key]) {
      clusters[key] = {
        key,
        items: [],
        sumLat: 0,
        sumLng: 0
      };
    }
    clusters[key].items.push(item);
    clusters[key].sumLat += lat;
    clusters[key].sumLng += lng;
  });

  return Object.values(clusters).map(c => {
    const count = c.items.length;
    const avgLat = c.sumLat / count;
    const avgLng = c.sumLng / count;
    const rep = c.items[0];
    return {
      key: c.key,
      position: [avgLat, avgLng],
      count,
      items: c.items,
      representative: rep
    };
  });
}

function createProvinceClusterIcon(region, count, tcColor = '#E85D2F') {
  const size = 52;
  const icon = L.divIcon({
    className: 'festv-province-cluster-icon',
    html: `
      <div class="festv-province-cluster" style="--cluster-color:${tcColor}; width:${size}px; height:${size}px;">
        <span class="cluster-bg"></span>
        <span class="cluster-label">${escapeHtml(region)}</span>
        <span class="cluster-count">${count}</span>
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
  return icon;
}
function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function isKopisGroupedItem(item) {
  return item && Array.isArray(item.performances) && item.performances.length > 0;
}

function groupedItemLabel(item) {
  if (item?.eventType === 'EV01') return '\uCD95\uC81C';
  if (item?.eventType === 'EV03') return '\uD589\uC0AC';
  return '\uACF5\uC5F0';
}

function getPinDarkColor(type) {
  return {
    EV01: '#C44A22',
    EV02: '#6230C0',
    EV03: '#096F55',
    STD: '#1F2937',
    TOUR: '#1459A8',
  }[type] || '#1F2937';
}

const mapPinIconCache = new Map();

function createMapPinIcon(item, selected = false, dense = false) {
  const type = item.eventType || 'TOUR';
  const tc = type === 'TOUR'
    ? { label: '\uAD00\uAD11', emoji: '\uD83D\uDDFA\uFE0F', color: '#1A6ECC' }
    : getTC(type);
  const size = selected ? 38 : 30;
  const title = escapeHtml(item.title);
  const performanceCount = isKopisGroupedItem(item) ? (item.performanceCount || item.performances.length) : 0;
  const performancePreview = isKopisGroupedItem(item)
    ? item.performances.slice(0, 4).map(p => p.title).filter(Boolean)
    : [];
  const cacheKey = `${type}|${selected ? 'selected' : 'idle'}|${dense ? 'dense' : 'full'}|${tc.color}|${tc.emoji}|${tc.label}|${title}|${performanceCount}|${performancePreview.join(',')}`;
  if (mapPinIconCache.has(cacheKey)) return mapPinIconCache.get(cacheKey);
  const previewHtml = performancePreview.length
    ? `<span class="festv-map-pin-tooltip-list">${performancePreview.map(p => `<em>${escapeHtml(p)}</em>`).join('')}</span>`
    : '';
  const countBadge = performanceCount > 0
    ? `<span class="festv-map-pin-count">${performanceCount}</span>`
    : '';
  const tooltip = `<span class="festv-map-pin-tooltip"><b>${escapeHtml(tc.label)}</b>${title}${previewHtml}</span>`;
  const icon = L.divIcon({
    className: `festv-map-pin-icon ${selected ? 'festv-map-pin-icon-selected' : ''}`,
    html: `
      <div class="festv-map-pin ${selected ? 'is-selected' : ''} ${dense ? 'is-dense' : ''}" style="--pin-color:${tc.color};--pin-dark:${getPinDarkColor(type)};--pin-size:${size}px;">
        ${selected ? '<span class="festv-map-pin-ripple"></span>' : ''}
        <span class="festv-map-pin-body"><span class="festv-map-pin-emoji">${tc.emoji}</span>${countBadge}</span>
        ${tooltip}
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size],
  });
  mapPinIconCache.set(cacheKey, icon);
  return icon;
}

function createNearbyPinIcon(item, selected = false) {
  const cacheItem = { ...item, eventType: 'TOUR' };
  return createMapPinIcon(cacheItem, selected, false);
}

const KOREA_BOUNDS = [[32.8, 124.5], [39.7, 132.0]];
const KOREA_CENTER = [37.34, 127.92];
const KOREA_DEFAULT_ZOOM = 8;
const PANEL_MIN_WIDTH = 260;
const PANEL_MAX_WIDTH = 520;

function clampPanelWidth(width) {
  return Math.min(PANEL_MAX_WIDTH, Math.max(PANEL_MIN_WIDTH, width));
}

function MapController({ selectedMapItem, selectedMarkerKey, markerRefs, mapRef, setMapZoom, setMapBounds }) {
  const map = useMap();
  const lastFocusedMarkerKeyRef = useRef(null);
  useEffect(() => { mapRef.current = map; }, [map, mapRef]);
  useEffect(() => {
    const syncZoomAndBounds = () => {
      setMapZoom(map.getZoom());
      setMapBounds(map.getBounds());
    };
    syncZoomAndBounds();
    map.on('zoomend', syncZoomAndBounds);
    map.on('moveend', syncZoomAndBounds);
    return () => {
      map.off('zoomend', syncZoomAndBounds);
      map.off('moveend', syncZoomAndBounds);
    };
  }, [map, setMapZoom, setMapBounds]);

  useEffect(() => {
    const handleMapChangeForHistory = () => {
      if (!window.history.state || window.history.state.type !== 'search') return;
      const center = map.getCenter();
      const zoom = map.getZoom();
      const nextState = {
        ...window.history.state,
        mapCenter: [center.lat, center.lng],
        mapZoom: zoom
      };
      window.history.replaceState(nextState, '', window.location.search);
    };
    map.on('zoomend', handleMapChangeForHistory);
    map.on('moveend', handleMapChangeForHistory);
    return () => {
      map.off('zoomend', handleMapChangeForHistory);
      map.off('moveend', handleMapChangeForHistory);
    };
  }, [map]);
  useEffect(() => {
    if (!selectedMapItem || !selectedMarkerKey) return;
    if (lastFocusedMarkerKeyRef.current === selectedMarkerKey) return;
    lastFocusedMarkerKeyRef.current = selectedMarkerKey;
    const lat = parseFloat(selectedMapItem.mapy);
    const lng = parseFloat(selectedMapItem.mapx);
    if (isNaN(lat) || isNaN(lng)) return;
    const zoom = Math.max(map.getZoom(), 10);
    const size = map.getSize();
    const yOffset = Math.min(120, Math.max(70, size.y * 0.14));
    const projected = map.project([lat, lng], zoom);
    const adjustedCenter = map.unproject(projected.subtract([0, yOffset]), zoom);
    map.flyTo(adjustedCenter, zoom, { duration: 0.5 });
    const marker = markerRefs.current[selectedMarkerKey];
    if (SHOW_MAP_POPUPS && marker) setTimeout(() => marker.openPopup(), 300);
  }, [selectedMapItem, selectedMarkerKey, map, markerRefs]);
  return null;
}

function getPopupClassName(zoom) {
  if (zoom <= 8) return 'popup popup-compact';
  if (zoom <= 10) return 'popup popup-medium';
  return 'popup';
}

function getPopupMaxWidth(zoom, base = 300) {
  if (zoom <= 8) return Math.min(base, 220);
  if (zoom <= 10) return Math.min(base, 260);
  return base;
}

const SHOW_MAP_POPUPS = false;

function getToday() { return new Date().toISOString().slice(0, 10); }
function getMonthsLater(m) {
  const d = new Date();
  d.setMonth(d.getMonth() + m);
  return d.toISOString().slice(0, 10);
}
function formatDate(s) {
  if (!s || s.length < 8) return s || '';
  return s.substring(0, 4) + '-' + s.substring(4, 6) + '-' + s.substring(6, 8);
}
function stripHtml(html) {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
}

function renderLink(raw) {
  if (!raw) return null;
  if (raw.includes('<a ') || raw.includes('<A ')) {
    const modified = raw.replace(/<a /gi, '<a target="_blank" rel="noopener noreferrer" ');
    return <span dangerouslySetInnerHTML={{ __html: modified }} />;
  }
  const text = stripHtml(raw).trim();
  const urlPattern = /https?:\/\/[^\s]+/g;
  const urls = text.match(urlPattern);
  if (urls) {
    const parts = text.split(urlPattern);
    const nodes = [];
    parts.forEach((part, i) => {
      if (part) nodes.push(<span key={`t${i}`}>{part}</span>);
      if (urls[i]) nodes.push(
        <a key={`u${i}`} href={urls[i]} target="_blank" rel="noopener noreferrer"
           style={{ color: '#1B4FD8', wordBreak: 'break-all' }}>
          {urls[i]}
        </a>
      );
    });
    return <>{nodes}</>;
  }
  return <span>{text}</span>;
}

const SOURCE_META = {
  KTO_TOURAPI: {
    provider: '\uD55C\uAD6D\uAD00\uAD11\uACF5\uC0AC',
    dataset: 'TourAPI',
    license: '\uACF5\uACF5\uB370\uC774\uD130 / \uACF5\uACF5\uB204\uB9AC \uC720\uD615\uC5D0 \uB530\uB984',
    licenseUrl: 'https://www.data.go.kr/ugs/selectPortalPolicyView.do',
    disclaimer: '\uD55C\uAD6D\uAD00\uAD11\uACF5\uC0AC TourAPI \uB370\uC774\uD130\uB97C \uAC80\uC0C9\uACFC \uC9C0\uB3C4 \uD45C\uC2DC\uB97C \uC704\uD574 \uC815\uADDC\uD654\uD588\uC2B5\uB2C8\uB2E4.',
  },
  KOPIS: {
    provider: '\uC608\uC220\uACBD\uC601\uC9C0\uC6D0\uC13C\uD130 / KOPIS',
    dataset: '\uACF5\uC5F0\uC608\uC220\uD1B5\uD569\uC804\uC0B0\uB9DD',
    license: '\uC81C\uACF5\uAE30\uAD00 \uC774\uC6A9\uC870\uAC74\uC5D0 \uB530\uB984',
    licenseUrl: 'https://www.kopis.or.kr',
    disclaimer: 'KOPIS \uACF5\uC5F0/\uC2DC\uC124 \uC815\uBCF4\uB97C \uC2DC\uC124 \uAE30\uC900\uC73C\uB85C \uBB36\uC5B4 \uD45C\uC2DC\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.',
  },
  SEOUL_CULTURE: {
    provider: '\uC11C\uC6B8\uD2B9\uBCC4\uC2DC',
    dataset: '\uC11C\uC6B8\uC2DC \uBB38\uD654\uD589\uC0AC\uC815\uBCF4',
    license: '\uCD9C\uCC98\uD45C\uC2DC \uC870\uAC74 \uACF5\uACF5\uB370\uC774\uD130',
    licenseUrl: 'https://data.seoul.go.kr/dataList/OA-15486/S/1/datasetView.do',
    disclaimer: '\uC11C\uC6B8\uD2B9\uBCC4\uC2DC \uACF5\uACF5\uB370\uC774\uD130\uB97C \uAC80\uC0C9\uACFC \uC9C0\uB3C4 \uD45C\uC2DC\uB97C \uC704\uD574 \uC815\uADDC\uD654\uD588\uC2B5\uB2C8\uB2E4.',
  },
  GYEONGGI_CULTURE: {
    provider: '\uACBD\uAE30\uB3C4 / \uACBD\uAE30\uB370\uC774\uD130\uB4DC\uB9BC',
    dataset: 'GGCULFOUEVENSTM',
    license: '\uCD9C\uCC98\uD45C\uC2DC \uC870\uAC74 \uACF5\uACF5\uB370\uC774\uD130',
    licenseUrl: 'https://data.gg.go.kr/portal/userAgreementPage.do',
    disclaimer: '\uACBD\uAE30\uB3C4 \uACF5\uACF5\uB370\uC774\uD130\uB97C \uAC80\uC0C9\uACFC \uC9C0\uB3C4 \uD45C\uC2DC\uB97C \uC704\uD574 \uC815\uADDC\uD654\uD588\uC2B5\uB2C8\uB2E4.',
  },
  IFAC_CULTURE: {
    provider: '\uC778\uCC9C\uBB38\uD654\uC7AC\uB2E8',
    dataset: '\uBB38\uD654\uC608\uC220\uD589\uC0AC / \uBB38\uD654\uAE30\uBC18\uC2DC\uC124 API',
    license: '\uC81C\uACF5\uAE30\uAD00 \uC774\uC6A9\uC870\uAC74\uC5D0 \uB530\uB984',
    licenseUrl: 'https://ifac.or.kr/infoDisclosure/openApi/usageGuide.do?key=m2501152968490',
    disclaimer: '\uC778\uCC9C\uBB38\uD654\uC7AC\uB2E8 \uBB38\uD654\uC608\uC220\uD589\uC0AC \uB370\uC774\uD130\uB97C \uC2DC\uC124 \uC815\uBCF4\uC640 \uB9E4\uCE6D\uD574 \uC9C0\uB3C4 \uD45C\uC2DC\uB97C \uC704\uD574 \uC815\uADDC\uD654\uD588\uC2B5\uB2C8\uB2E4.',
  },
};

function getSourceCode(item) {
  if (item?.apiSource) return item.apiSource;
  if (item?.contentId?.startsWith('KOPIS-')) return 'KOPIS';
  if (item?.contentId?.startsWith('SEOUL-')) return 'SEOUL_CULTURE';
  if (item?.contentId?.startsWith('GG-')) return 'GYEONGGI_CULTURE';
  if (item?.contentId?.startsWith('IFAC-')) return 'IFAC_CULTURE';
  return 'KTO_TOURAPI';
}

function getSourceMeta(item) {
  const code = getSourceCode(item);
  return {
    code,
    ...(SOURCE_META[code] || {
      provider: item?.sourceName || code,
      dataset: item?.sourceCategory || code,
      license: '\uC81C\uACF5\uAE30\uAD00 \uC774\uC6A9\uC870\uAC74\uC5D0 \uB530\uB984',
      disclaimer: '\uC6D0\uCC9C \uB370\uC774\uD130\uB97C \uAC80\uC0C9\uACFC \uC9C0\uB3C4 \uD45C\uC2DC\uB97C \uC704\uD574 \uC815\uADDC\uD654\uD588\uC2B5\uB2C8\uB2E4.',
    }),
  };
}

function SourceAttribution({ item }) {
  const meta = getSourceMeta(item);
  const sourceTitle = item.sourceName || meta.dataset || meta.provider;
  const originalUrl = item.sourceUrl || meta.licenseUrl || item.homepage || item.eventhomepage;
  return (
    <div className="source-attribution">
      <div className="source-attribution-summary">
        <span className="source-chip">{sourceTitle}</span>
        {item.sourceRegion && <span className="source-subtle">{item.sourceRegion}</span>}
        {item.sourceCategory && <span className="source-subtle">{item.sourceCategory}</span>}
      </div>
      <details className="source-panel">
        <summary>{'\uCD9C\uCC98 \uC0C1\uC138\uBCF4\uAE30'}</summary>
        <div className="source-panel-body">
          <div className="source-panel-row"><b>{'\uC81C\uACF5'}</b><span>{meta.provider}</span></div>
          <div className="source-panel-row"><b>{'\uB370\uC774\uD130'}</b><span>{sourceTitle}</span></div>
          {meta.code && <div className="source-panel-row"><b>{'API'}</b><span>{meta.code}</span></div>}
          {item.sourceRegion && <div className="source-panel-row"><b>{'\uC9C0\uC5ED'}</b><span>{item.sourceRegion}</span></div>}
          {item.sourceCategory && <div className="source-panel-row"><b>{'\uBD84\uB958'}</b><span>{item.sourceCategory}</span></div>}
          <div className="source-panel-row"><b>{'\uC774\uC6A9\uC870\uAC74'}</b><span>{meta.license}</span></div>
          {meta.licenseUrl && <div className="source-panel-row"><b>{'\uAE30\uC900'}</b><span>{renderLink(meta.licenseUrl)}</span></div>}
          {originalUrl && <div className="source-panel-row"><b>{'\uC6D0\uBB38'}</b><span>{renderLink(originalUrl)}</span></div>}
          <div className="source-disclaimer">
            {meta.disclaimer} {'\uC6D0\uCC9C \uC815\uBCF4\uC640 \uCC28\uC774\uAC00 \uC788\uC744 \uC218 \uC788\uC73C\uBBC0\uB85C \uBC29\uBB38 \uC804 \uC6D0\uBB38\uC744 \uD655\uC778\uD574 \uC8FC\uC138\uC694.'}
          </div>
        </div>
      </details>
    </div>
  );
}

function getFestivalKey(item) {
  return item ? `${item.eventType || 'UNKNOWN'}:${item.id}` : '';
}

function mergeDetailData(current, incoming) {
  if (!incoming) return current;
  const merged = { ...current };
  Object.entries(incoming).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return;
    if (Array.isArray(value) && value.length === 0 && Array.isArray(current[key]) && current[key].length > 0) return;
    merged[key] = value;
  });
  return merged;
}

function CollapsibleText({ text, lines = 3 }) {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) {
      setOverflows(ref.current.scrollHeight > ref.current.clientHeight + 2);
    }
  }, [text]);
  if (!text) return null;
  return (
    <div>
      <div
        ref={ref}
        style={expanded
          ? { lineHeight: 1.65 }
          : { overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: lines, WebkitBoxOrient: 'vertical', lineHeight: 1.65 }
        }
      >
        {text}
      </div>
      {(overflows || expanded) && (
        <button
          onClick={() => setExpanded(v => !v)}
          style={{
            background: 'none', border: 'none', padding: '3px 0 0',
            color: '#1B4FD8', fontSize: 11, cursor: 'pointer', fontWeight: 600,
          }}
        >
          {expanded ? '\uC811\uAE30 \u25B2' : '\uB354\uBCF4\uAE30 \u25BC'}
        </button>
      )}
    </div>
  );
}

export default function App() {
  const initialParamsRef = useRef(new URLSearchParams(window.location.search));
  const sessionIdRef = useRef('');
  const sessionStartRef = useRef(Date.now());

  // 텔레메트리 이벤트 트래킹 함수
  const trackEvent = useCallback(async (eventType, targetId, targetName) => {
    const sessionId = sessionIdRef.current;
    if (!sessionId) return;
    try {
      await axios.post(`${API_BASE}/api/analytics/event`, {
        sessionId,
        eventType,
        targetId: targetId ? String(targetId) : null,
        targetName: targetName ? String(targetName) : null
      });
    } catch (e) {
      console.warn('[Telemetry] Event tracking failed:', e.message);
    }
  }, []);

  // 세션 등록 및 visibilitychange/beforeunload 심박 전송
  useEffect(() => {
    let sId = sessionStorage.getItem('playpin_session_id');
    if (!sId) {
      sId = generateUUID();
      sessionStorage.setItem('playpin_session_id', sId);
    }
    sessionIdRef.current = sId;

    // 세션 시작 보고
    axios.post(`${API_BASE}/api/analytics/session`, { sessionId: sId })
      .catch(e => console.warn('[Telemetry] Session start failed:', e.message));

    // 주기적인 heartbeat (30초마다)
    const interval = setInterval(() => {
      const duration = Math.round((Date.now() - sessionStartRef.current) / 1000);
      axios.post(`${API_BASE}/api/analytics/session/heartbeat`, {
        sessionId: sId,
        duration
      }).catch(() => {});
    }, 30000);

    // 페이지 나갈 때 최종 체류 시간 전송 (sendBeacon 사용)
    const sendFinalDuration = () => {
      const duration = Math.round((Date.now() - sessionStartRef.current) / 1000);
      const url = `${API_BASE}/api/analytics/session/heartbeat`;
      const blob = new Blob([JSON.stringify({ sessionId: sId, duration })], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        sendFinalDuration();
      }
    };

    window.addEventListener('beforeunload', sendFinalDuration);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // 최초 진입 시 PAGE_VIEW 로깅
    trackEvent('PAGE_VIEW', window.location.pathname, '메인 지도 진입');

    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', sendFinalDuration);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [trackEvent]);

  // 브라우저 뒤로가기/앞으로가기 (popstate) 감지 처리
  useEffect(() => {
    const handlePopState = (e) => {
      if (e.state && e.state.type === 'search') {
        const { keyword: sk, region: sr, eventType: se, startDate: sd, endDate: ed, selectedKey: skey, mapCenter: scenter, mapZoom: szoom } = e.state;
        setKeyword(sk || '');
        setRegion(sr || '');
        setEventType(se || 'EV01');
        setStartDate(sd || getToday());
        setEndDate(ed || getMonthsLater(3));

        if (mapRef.current && scenter && szoom) {
          mapRef.current.setView(scenter, szoom);
        }

        selectedKeyRef.current = skey;
        setSelectedKey(skey);
        if (skey) {
          setDetailCollapsed(false);
        } else {
          clearSelection(false);
        }

        fetchFestivals({
          keyword: sk || '',
          region: sr || '',
          eventType: se || 'EV01',
          startDate: sd || getToday(),
          endDate: ed || getMonthsLater(3)
        });
      } else {
        clearSelection(false);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const initialKeyword = initialParamsRef.current.get('keyword') || '';
  const initialRegion = initialParamsRef.current.get('region') || '';
  const initialType = initialParamsRef.current.get('type') || 'EV01';
  const initialStartDate = initialParamsRef.current.get('startDate') || getToday();
  const initialEndDate = initialParamsRef.current.get('endDate') || getMonthsLater(3);
  const [festivals, setFestivals] = useState([]);
  const [keyword, setKeyword] = useState(initialKeyword);
  const [region, setRegion] = useState(initialRegion);
  const [eventType, setEventType] = useState(initialType);     // Initial view: festival only.
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [message, setMessage] = useState('');
  const [selectedKey, setSelectedKey] = useState(null);
  const [detailTab, setDetailTab] = useState('info');
  const [loadingDetailKey, setLoadingDetailKey] = useState(null);
  const [loadingNearbyId, setLoadingNearbyId] = useState(null);
  const [nearbyAttractions, setNearbyAttractions] = useState([]);
  const [nearbyDetailInfos, setNearbyDetailInfos] = useState({});
  const [nearbyRadius, setNearbyRadius] = useState(10);
  const [expandedAttrId, setExpandedAttrId] = useState(null);
  const [selectedNearbyId, setSelectedNearbyId] = useState(null);
  const [mapZoom, setMapZoom] = useState(7);
  const [mapBounds, setMapBounds] = useState(null);
  const [panelWidth, setPanelWidth] = useState(300);
  const [resizingPanel, setResizingPanel] = useState(false);
  const [searchCollapsed, setSearchCollapsed] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [detailCollapsed, setDetailCollapsed] = useState(false);

  const markerRefs = useRef({});
  const gridItemRefs = useRef({});
  const nearbyItemRefs = useRef({});
  const selectSource = useRef(null);
  const selectedKeyRef = useRef(null);
  const selectedNearbyIdRef = useRef(null);
  const suppressNearbyPopupCloseRef = useRef(false);
  const suppressPopupCloseRef = useRef(false);
  const detailTabRef = useRef(detailTab);
  const mapRef = useRef(null);
  const resizeStartRef = useRef({ x: 0, width: 300 });

  /* Keep the list aligned with the active category filter. */
  const displayedFestivals = eventType
    ? festivals.filter(f => f.eventType === eventType)
    : festivals;

  useEffect(() => {
    if (!window.history.state) {
      window.history.replaceState({
        type: 'search',
        keyword: initialKeyword,
        region: initialRegion,
        eventType: initialType,
        startDate: initialStartDate,
        endDate: initialEndDate,
        selectedKey: null,
        mapCenter: KOREA_CENTER,
        mapZoom: KOREA_DEFAULT_ZOOM
      }, '', window.location.search || '?');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPanelResize = useCallback((e) => {
    e.preventDefault();
    resizeStartRef.current = { x: e.clientX, width: panelWidth };
    setResizingPanel(true);
  }, [panelWidth]);

  useEffect(() => {
    if (!resizingPanel) return undefined;
    const handleMove = (e) => {
      const delta = resizeStartRef.current.x - e.clientX;
      setPanelWidth(clampPanelWidth(resizeStartRef.current.width + delta));
    };
    const handleUp = () => setResizingPanel(false);
    document.body.classList.add('panel-resizing');
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      document.body.classList.remove('panel-resizing');
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [resizingPanel]);

  const fetchFestivals = useCallback(async (overrides = {}) => {
    try {
      const params = {};
      const nextKeyword = Object.prototype.hasOwnProperty.call(overrides, 'keyword') ? overrides.keyword : keyword;
      const nextRegion = Object.prototype.hasOwnProperty.call(overrides, 'region') ? overrides.region : region;
      const nextEventType = Object.prototype.hasOwnProperty.call(overrides, 'eventType') ? overrides.eventType : eventType;
      const nextStartDate = Object.prototype.hasOwnProperty.call(overrides, 'startDate') ? overrides.startDate : startDate;
      const nextEndDate = Object.prototype.hasOwnProperty.call(overrides, 'endDate') ? overrides.endDate : endDate;
      if (nextKeyword) params.keyword = nextKeyword;
      if (nextRegion) params.region = nextRegion;
      if (nextEventType) params.type = nextEventType;
      if (nextStartDate) params.startDate = nextStartDate.replace(/-/g, '');
      if (nextEndDate) params.endDate = nextEndDate.replace(/-/g, '');
      const res = await axios.get(`${API_BASE}/api/festivals`, { params });
      setFestivals(res.data);
      setMessage('');
    } catch (e) {
      setMessage('\uB370\uC774\uD130\uB97C \uBD88\uB7EC\uC624\uB294 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.');
    }
  }, [keyword, region, eventType, startDate, endDate]);

  const clearSelection = (triggerHistoryBack = true) => {
    selectedKeyRef.current = null;
    setSelectedKey(null);
    setDetailTab('info');
    setNearbyAttractions([]);
    setNearbyDetailInfos({});
    setExpandedAttrId(null);
    setSelectedNearbyId(null);
    selectedNearbyIdRef.current = null;
    selectSource.current = null;
    if (mapRef.current) {
      mapRef.current.closePopup();
    }
    if (triggerHistoryBack && window.history.state && window.history.state.type === 'detail') {
      window.history.back();
    } else if (triggerHistoryBack) {
      const params = new URLSearchParams(window.location.search);
      if (params.has('selected')) {
        params.delete('selected');
        window.history.pushState(null, '', `?${params.toString()}`);
      }
    }
  };

  useEffect(() => { fetchFestivals(); }, [fetchFestivals]);
  useEffect(() => { detailTabRef.current = detailTab; }, [detailTab]);

  const enrichDetail = useCallback(async (key) => {
    const f = festivals.find(x => getFestivalKey(x) === key);
    if (!f || !f.contentId || !f.eventType) return;
    if (f.eventType === 'STD') return;
    if (isKopisGroupedItem(f)) return;
    if (f.overview && f.detailInfos) return;
    setLoadingDetailKey(key);
    trackEvent('VIEW_DETAIL', f.contentId, f.title);
    try {
      const res = await axios.get(`${API_BASE}/api/festivals/${f.eventType}/${f.contentId}`);
      if (!res.data) return;
      setFestivals(prev => prev.map(x => getFestivalKey(x) === key ? mergeDetailData(x, res.data) : x));
    } catch (e) {
      console.warn('detail enrich failed:', e.message);
    } finally {
      setLoadingDetailKey(null);
    }
  }, [festivals, trackEvent]);

  const pushHistoryState = (override = {}) => {
    let currentCenter = KOREA_CENTER;
    let currentZoom = KOREA_DEFAULT_ZOOM;
    if (mapRef.current) {
      const center = mapRef.current.getCenter();
      currentCenter = [center.lat, center.lng];
      currentZoom = mapRef.current.getZoom();
    }

    const stateKeyword = override.hasOwnProperty('keyword') ? override.keyword : keyword;
    const stateRegion = override.hasOwnProperty('region') ? override.region : region;
    const stateEventType = override.hasOwnProperty('eventType') ? override.eventType : eventType;
    const stateStartDate = override.hasOwnProperty('startDate') ? override.startDate : startDate;
    const stateEndDate = override.hasOwnProperty('endDate') ? override.endDate : endDate;
    const stateSelectedKey = override.hasOwnProperty('selectedKey') ? override.selectedKey : selectedKey;
    const stateCenter = override.hasOwnProperty('mapCenter') ? override.mapCenter : currentCenter;
    const stateZoom = override.hasOwnProperty('mapZoom') ? override.mapZoom : currentZoom;

    const params = new URLSearchParams();
    if (stateKeyword) params.set('keyword', stateKeyword);
    if (stateRegion) params.set('region', stateRegion);
    if (stateEventType) params.set('type', stateEventType);
    if (stateStartDate) params.set('startDate', stateStartDate);
    if (stateEndDate) params.set('endDate', stateEndDate);
    if (stateSelectedKey) params.set('selected', stateSelectedKey);

    const nextState = {
      type: 'search',
      keyword: stateKeyword,
      region: stateRegion,
      eventType: stateEventType,
      startDate: stateStartDate,
      endDate: stateEndDate,
      selectedKey: stateSelectedKey,
      mapCenter: stateCenter,
      mapZoom: stateZoom
    };

    const isSameState = window.history.state &&
      window.history.state.keyword === stateKeyword &&
      window.history.state.region === stateRegion &&
      window.history.state.eventType === stateEventType &&
      window.history.state.selectedKey === stateSelectedKey &&
      JSON.stringify(window.history.state.mapCenter) === JSON.stringify(stateCenter) &&
      window.history.state.mapZoom === stateZoom;

    if (isSameState) {
      window.history.replaceState(nextState, '', `?${params.toString()}`);
    } else {
      window.history.pushState(nextState, '', `?${params.toString()}`);
    }
  };

  const handleMarkerSelect = (item) => {
    if (!item) return;
    const key = getFestivalKey(item);
    selectSource.current = 'map';
    selectedKeyRef.current = key;
    setSelectedKey(key);
    setDetailTab('info');
    setNearbyAttractions([]);
    setNearbyDetailInfos({});
    setExpandedAttrId(null);
    setSelectedNearbyId(null);
    selectedNearbyIdRef.current = null;
    setDetailCollapsed(false);
    pushHistoryState({ selectedKey: key });
    const el = gridItemRefs.current[key];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    enrichDetail(key);
  };

  const handleGridSelect = (item) => {
    if (!item) return;
    const key = getFestivalKey(item);
    selectSource.current = 'list';
    selectedKeyRef.current = key;
    setSelectedKey(key);
    setDetailTab('info');
    setNearbyAttractions([]);
    setNearbyDetailInfos({});
    setExpandedAttrId(null);
    setSelectedNearbyId(null);
    selectedNearbyIdRef.current = null;
    setDetailCollapsed(false);
    pushHistoryState({ selectedKey: key });
    enrichDetail(key);
  };

  const fetchNearbyAttractions = async (lat, lng, radius) => {
    try {
      const res = await axios.get(`${API_BASE}/api/tourist-attraction/nearby`, { params: { lat, lng, radius } });
      setNearbyAttractions(res.data);
      setNearbyDetailInfos({});
      setExpandedAttrId(null);
      setSelectedNearbyId(null);
      selectedNearbyIdRef.current = null;
    } catch (e) {
      setNearbyAttractions([]);
    }
  };

  const handleNearbyTab = (radius) => {
    const shouldFetch = nearbyAttractions.length === 0 || nearbyRadius !== radius;
    setDetailTab('nearby');
    setNearbyRadius(radius);
    const f = festivals.find(f => getFestivalKey(f) === selectedKey);
    if (shouldFetch && f && f.mapy && f.mapx) fetchNearbyAttractions(parseFloat(f.mapy), parseFloat(f.mapx), radius);
  };

  const selectedFestival = (() => {
    return displayedFestivals.find(f => getFestivalKey(f) === selectedKey) || null;
  })();

  const selectedNearbyAttraction = detailTab === 'nearby'
    ? nearbyAttractions.find(a => a.id === selectedNearbyId) || null
    : null;
  const selectedMapItem = selectedNearbyAttraction || selectedFestival;
  const selectedMarkerKey = selectedNearbyAttraction
    ? `tourist-${selectedNearbyAttraction.id}`
    : selectedFestival
    ? getFestivalKey(selectedFestival)
    : null;

  const handlePopupClose = (key) => {
    setTimeout(() => {
      if (suppressPopupCloseRef.current) return;
      if (detailTabRef.current === 'nearby') return;
      if (selectedKeyRef.current === key) clearSelection();
    }, 0);
  };

  const handleNearbySelect = (attraction) => {
    if (!attraction) return;
    const nextId = attraction.id;
    const isSelected = selectedNearbyId === nextId;
    setExpandedAttrId(isSelected ? null : nextId);
    setSelectedNearbyId(isSelected ? null : nextId);
    selectedNearbyIdRef.current = isSelected ? null : nextId;
    if (isSelected) {
      if (mapRef.current) mapRef.current.closePopup();
      return;
    }
    fetchNearbyDetailInfos(attraction);
    const el = nearbyItemRefs.current[nextId];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleNearbyPopupClose = (id) => {
    setTimeout(() => {
      if (suppressPopupCloseRef.current) return;
      if (suppressNearbyPopupCloseRef.current) return;
      if (selectedNearbyIdRef.current === id) {
        selectedNearbyIdRef.current = null;
        setSelectedNearbyId(null);
      }
    }, 0);
  };

  const detailData = selectedKey != null ? festivals.find(f => getFestivalKey(f) === selectedKey) || null : null;
  const detailTC = detailData ? getTC(detailData.eventType) : null;

  useEffect(() => {
    if (!selectedKey) return;
    if (displayedFestivals.some(f => getFestivalKey(f) === selectedKey)) return;
    selectedKeyRef.current = null;
    setSelectedKey(null);
    setDetailTab('info');
    setNearbyAttractions([]);
    setNearbyDetailInfos({});
    setExpandedAttrId(null);
    setSelectedNearbyId(null);
    selectedNearbyIdRef.current = null;
    selectSource.current = null;
    if (mapRef.current) mapRef.current.closePopup();
  }, [displayedFestivals, selectedKey]);

  const topTouchRef = useRef({ startY: 0, deltaY: 0 });
  const handleTopTouchStart = (e) => {
    topTouchRef.current.startY = e.touches[0].clientY;
    topTouchRef.current.deltaY = 0;
  };
  const handleTopTouchMove = (e) => {
    topTouchRef.current.deltaY = e.touches[0].clientY - topTouchRef.current.startY;
  };
  const handleTopTouchEnd = () => {
    const { deltaY } = topTouchRef.current;
    const threshold = 50;
    if (deltaY < -threshold) {
      setSearchCollapsed(true);
    } else if (deltaY > threshold) {
      setSearchCollapsed(false);
    }
  };

  const bottomTouchRef = useRef({ startY: 0, deltaY: 0 });
  const handleBottomTouchStart = (e) => {
    bottomTouchRef.current.startY = e.touches[0].clientY;
    bottomTouchRef.current.deltaY = 0;
  };
  const handleBottomTouchMove = (e) => {
    bottomTouchRef.current.deltaY = e.touches[0].clientY - bottomTouchRef.current.startY;
  };
  const handleBottomTouchEnd = () => {
    const { deltaY } = bottomTouchRef.current;
    const threshold = 50;
    if (deltaY > threshold) {
      setSidebarCollapsed(true);
    } else if (deltaY < -threshold) {
      setSidebarCollapsed(false);
    }
  };

  const detailTouchRef = useRef({ startY: 0, deltaY: 0 });
  const handleDetailTouchStart = (e) => {
    detailTouchRef.current.startY = e.touches[0].clientY;
    detailTouchRef.current.deltaY = 0;
  };
  const handleDetailTouchMove = (e) => {
    detailTouchRef.current.deltaY = e.touches[0].clientY - detailTouchRef.current.startY;
  };
  const handleDetailTouchEnd = () => {
    const { deltaY } = detailTouchRef.current;
    const threshold = 50;
    if (deltaY > threshold) {
      setDetailCollapsed(true);
    } else if (deltaY < -threshold) {
      setDetailCollapsed(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (keyword && keyword.trim()) {
      trackEvent('SEARCH', keyword.trim(), null);
    }
    setSearchCollapsed(true);
    setSidebarCollapsed(false);
    pushHistoryState({ keyword, selectedKey: null });
    fetchFestivals();
  };

  /* Changing category resets the current map/detail selection. */
  const handleCatPill = (type) => {
    const nextType = type || 'EV01';
    setEventType(nextType);
    selectedKeyRef.current = null;
    setSelectedKey(null);
    setDetailTab('info');
    setNearbyAttractions([]);
    setNearbyDetailInfos({});
    setExpandedAttrId(null);
    setSelectedNearbyId(null);
    selectedNearbyIdRef.current = null;
    selectSource.current = null;
    if (mapRef.current) mapRef.current.closePopup();
    setSearchCollapsed(true);
    setSidebarCollapsed(false);
    pushHistoryState({ eventType: nextType, selectedKey: null });
    fetchFestivals({ eventType: nextType });
  };

  const handleRegionChange = (e) => {
    const val = e.target.value;
    setRegion(val);
    pushHistoryState({ region: val, selectedKey: null });
  };

  const handleReset = async () => {
    const rs = getToday();
    const re = getMonthsLater(3);
    setKeyword(''); setRegion(''); setEventType('EV01');
    selectedKeyRef.current = null;
    setStartDate(rs); setEndDate(re);
    setSelectedKey(null); setDetailTab('info'); setNearbyAttractions([]);
    setNearbyDetailInfos({});
    setExpandedAttrId(null);
    setSelectedNearbyId(null);
    selectedNearbyIdRef.current = null;
    selectSource.current = null;
    if (mapRef.current) { mapRef.current.closePopup(); mapRef.current.setView(KOREA_CENTER, KOREA_DEFAULT_ZOOM); }
    pushHistoryState({ keyword: '', region: '', eventType: 'EV01', startDate: rs, endDate: re, selectedKey: null });
    try {
      const res = await axios.get(`${API_BASE}/api/festivals`, {
        params: { type: 'EV01', startDate: rs.replace(/-/g, ''), endDate: re.replace(/-/g, '') },
      });
      setFestivals(res.data);
      setMessage('');
    } catch (e) {
      setMessage('\uB370\uC774\uD130\uB97C \uBD88\uB7EC\uC624\uB294 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.');
    }
  };

  /* Detail navigation follows the filtered list order. */
  const selectedIdx = displayedFestivals.findIndex(f => getFestivalKey(f) === selectedKey);
  const densePins = displayedFestivals.length > 300;

  const fetchNearbyDetailInfos = async (a) => {
    if (!a || !a.contentId || nearbyDetailInfos[a.contentId]) return;
    setLoadingNearbyId(a.contentId);
    try {
      const { data } = await axios.get(`${API_BASE}/api/tourist-attraction/${a.contentId}`);
      setNearbyAttractions(prev => prev.map(item => (
        item.contentId === a.contentId ? { ...item, ...data } : item
      )));
      setNearbyDetailInfos(prev => ({ ...prev, [a.contentId]: data.detailInfos || [] }));
    } catch (e) {
      setNearbyDetailInfos(prev => ({ ...prev, [a.contentId]: [] }));
    } finally {
      setLoadingNearbyId(null);
    }
  };

  return (
    <>
    <div className="layout">
      <div className="content">
        <aside className="sidebar-left">
          <span className="sidebar-left-label">AD</span>
          {[{ h: 220, bg: '#f0f4ff' }, { h: 160, bg: '#fff4f0' }, { h: 110, bg: '#f0fff4' }].map((b, i) => (
            <div key={i} className="sidebar-left-block"
              style={{ width: 62, height: b.h, background: `linear-gradient(160deg,${b.bg},#f8f9fa)` }}>
              <span>&#44305;&#44256; &#50689;&#50669;</span>
            </div>
          ))}
        </aside>

        <main className="map-area">
          <MapContainer center={KOREA_CENTER} zoom={KOREA_DEFAULT_ZOOM} minZoom={7}
            maxBounds={KOREA_BOUNDS} maxBoundsViscosity={1.0}>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; OpenStreetMap contributors'
            />
            <MapController selectedMapItem={selectedMapItem} selectedMarkerKey={selectedMarkerKey} markerRefs={markerRefs} mapRef={mapRef} setMapZoom={setMapZoom} setMapBounds={setMapBounds} />
            {mapZoom <= 8 ? (
              // 1. 전국 뷰: 광역시도별 클러스터 마커 렌더링
              (() => {
                const regionCounts = {};
                displayedFestivals.forEach(f => {
                  const reg = getFestivalRegion(f);
                  if (reg) {
                    regionCounts[reg] = (regionCounts[reg] || 0) + 1;
                  }
                });
                return Object.entries(REGION_CENTERS).map(([reg, center]) => {
                  const count = regionCounts[reg] || 0;
                  if (count === 0) return null;
                  const tc = getTC(eventType);
                  const activeColor = tc.color || '#E85D2F';
                  return (
                    <Marker
                      key={`cluster-${reg}`}
                      position={center}
                      icon={createProvinceClusterIcon(reg, count, activeColor)}
                      eventHandlers={{
                        click: () => {
                          if (mapRef.current) {
                            mapRef.current.flyTo(center, 9, { duration: 0.6 });
                          }
                          setRegion(reg);
                          pushHistoryState({ region: reg, selectedKey: null });
                          fetchFestivals({ region: reg });
                        }
                      }}
                    />
                  );
                });
              })()
            ) : (
              // 2. 적응형 뷰 (zoom >= 9): 뷰포트 내 핀 밀집 복잡도(임계값 30개)에 반응하여 클러스터링 동적 처리
              (() => {
                const viewportFestivals = displayedFestivals.filter(f => {
                  const lat = parseFloat(f.mapy);
                  const lng = parseFloat(f.mapx);
                  if (isNaN(lat) || isNaN(lng)) return false;
                  if (!mapBounds) return true; // fallback
                  return mapBounds.contains([lat, lng]);
                });

                if (viewportFestivals.length < 50) {
                  // 뷰포트 내 핀이 50개 미만으로 적으면: 낱개 상세 마커(이모지 핀)로 즉각 해산하여 렌더링
                  return viewportFestivals.map((f) => {
                    const lat = parseFloat(f.mapy);
                    const lng = parseFloat(f.mapx);
                    const isSelected = selectedKey === getFestivalKey(f);
                    return (
                      <Marker key={getFestivalKey(f)} position={[lat, lng]} icon={createMapPinIcon(f, isSelected, densePins && !isSelected)} zIndexOffset={isSelected ? 1000 : 0}
                        ref={(ref) => { if (ref) markerRefs.current[getFestivalKey(f)] = ref; }}
                        eventHandlers={{
                          click: () => handleMarkerSelect(f),
                          popupclose: () => handlePopupClose(getFestivalKey(f)),
                        }}>
                        {SHOW_MAP_POPUPS && (
                        <Popup maxWidth={getPopupMaxWidth(mapZoom, 300)}>
                          <div className={getPopupClassName(mapZoom)}>
                            {f.firstimage2 && <img src={f.firstimage2} alt={f.title} className="popup-img" />}
                            <strong>{f.title}</strong>
                            <p>{formatDate(f.eventStartDate)} ~ {formatDate(f.eventEndDate)}</p>
                            {f.addr1 && <p>{f.addr1}</p>}
                            {f.tel && <p>Tel: {f.tel}</p>}
                          </div>
                        </Popup>
                        )}
                      </Marker>
                    );
                  });
                } else {
                  // 뷰포트 내 핀이 30개 이상으로 많으면: 동적 그리드 클러스터링으로 뭉쳐서 렌더링
                  const clusters = getGridClusters(displayedFestivals, mapZoom, mapBounds) || [];
                  const tc = getTC(eventType);
                  const activeColor = tc.color || '#E85D2F';
                  return clusters.map(c => {
                    const localName = getRepresentativeLocalName(c.representative);
                    return (
                      <Marker
                        key={`grid-${c.key}`}
                        position={c.position}
                        icon={createProvinceClusterIcon(localName, c.count, activeColor)}
                        eventHandlers={{
                          click: () => {
                            if (mapRef.current) {
                              mapRef.current.flyTo(c.position, mapZoom + 1, { duration: 0.5 });
                            }
                          }
                        }}
                      />
                    );
                  });
                }
              })()
            )}
            {detailTab === 'nearby' && nearbyAttractions.map(a => {
              const lat = parseFloat(a.mapy);
              const lng = parseFloat(a.mapx);
              if (isNaN(lat) || isNaN(lng)) return null;
              return (
                <Marker
                  key={`tourist-${a.id}`}
                  position={[lat, lng]}
                  icon={createNearbyPinIcon(a, selectedNearbyId === a.id)}
                  zIndexOffset={selectedNearbyId === a.id ? 900 : 0}
                  ref={(ref) => { if (ref) markerRefs.current[`tourist-${a.id}`] = ref; }}
                  eventHandlers={{
                    click: () => handleNearbySelect(a),
                    popupclose: () => handleNearbyPopupClose(a.id),
                  }}
                >
                  {SHOW_MAP_POPUPS && (
                  <Popup maxWidth={getPopupMaxWidth(mapZoom, 250)}>
                    <div className={getPopupClassName(mapZoom)}>
                      {a.firstimage2 && <img src={a.firstimage2} alt={a.title} className="popup-img" />}
                      <strong>{a.title}</strong>
                      {a.addr1 && <p>{a.addr1}</p>}
                      {a.tel && <p>Tel: {a.tel}</p>}
                    </div>
                  </Popup>
                  )}
                </Marker>
              );
            })}
          </MapContainer>

          <div className={`search-overlay ${searchCollapsed ? 'search-collapsed' : ''}`}
            onTouchStart={handleTopTouchStart} onTouchMove={handleTopTouchMove} onTouchEnd={handleTopTouchEnd}>
            <form className="search-pill" onSubmit={handleSearch}>
              <div className="search-main-row">
                <span className="logo-text">PlayPin</span>
                <div className="search-divider" />
                <div className="search-input-wrap">
                  <svg className="search-input-icon" width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <circle cx="6" cy="6" r="4.5" stroke="#374151" strokeWidth="1.4"/>
                    <path d="M9.5 9.5L12.5 12.5" stroke="#374151" strokeWidth="1.4" strokeLinecap="round"/>
                  </svg>
                  <input type="text" value={keyword} onChange={e => setKeyword(e.target.value)} placeholder={'\uCD95\uC81C\u00B7\uD589\uC0AC\uBA85, \uC9C0\uC5ED \uAC80\uC0C9'} className="search-input" />
                </div>
                <button type="button" className="search-toggle-btn" onClick={() => setSearchCollapsed(!searchCollapsed)} aria-label="toggle filters" title="toggle filters">
                  {searchCollapsed ? (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                  ) : (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"></polyline></svg>
                  )}
                </button>
                <select value={region} onChange={handleRegionChange} className="search-region">
                  <option value="">&#51204;&#52404; &#51648;&#50669;</option>
                  {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <div className="search-inline-dates">
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="date-input" />
                  <span className="date-tilde">~</span>
                  <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="date-input" />
                </div>
                <button type="submit" className="search-btn">&#44160;&#49353;</button>
                <button type="button" className="reset-btn" onClick={handleReset}>&#52488;&#44592;&#54868;</button>
              </div>
            </form>
            <div className="cat-pills-row">
              {[{ type: 'EV01', ...TYPE_CONFIG.EV01 }, { type: 'EV02', ...TYPE_CONFIG.EV02 }, { type: 'EV03', ...TYPE_CONFIG.EV03 }].map(c => {
                const active = eventType === c.type;
                return (
                  <button key={c.type || 'all'} type="button" className="cat-pill" onClick={() => handleCatPill(c.type)}
                    style={{ background: active ? c.color : 'rgba(255,255,255,0.85)', color: active ? 'white' : (c.color || '#374151'), borderColor: active ? c.color : (c.color ? `${c.color}50` : 'rgba(0,0,0,0.12)'), fontWeight: active ? 600 : 500, boxShadow: active ? `0 2px 8px ${c.color}40` : '0 1px 4px rgba(0,0,0,0.08)' }}>
                    {c.emoji && <span style={{ fontSize: 11 }}>{c.emoji}</span>}{c.label}
                  </button>
                );
              })}
            </div>
            {message && <div className={`message ${message.includes('\uC624\uB958') || message.includes('\uC2E4\uD328') ? 'msg-error' : 'msg-success'}`}>{message}</div>}
          </div>
        </main>

        <div className="panel-resize-handle" onMouseDown={startPanelResize} title="resize panel" />
        <aside className={`sidebar-right ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`} style={{ width: panelWidth }}>
          <div className="sidebar-header" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            onTouchStart={handleBottomTouchStart} onTouchMove={handleBottomTouchMove} onTouchEnd={handleBottomTouchEnd}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div className="sidebar-header-title">&#44160;&#49353; &#44208;&#44284;</div>
              <div className="sidebar-header-count">&#52509; <strong>{displayedFestivals.length}&#44148;</strong>{eventType && festivals.length !== displayedFestivals.length && <span style={{ color: '#d1d5db', margin: '0 5px' }}>&middot;</span>}{eventType && festivals.length !== displayedFestivals.length && <span>&#51204;&#52404; {festivals.length}&#44148;</span>}</div>
            </div>
            <button type="button" className="sidebar-toggle-btn" aria-label="toggle list visibility" title="toggle list visibility">
              {sidebarCollapsed ? (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"></polyline></svg>
              ) : (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
              )}
            </button>
          </div>
          <div className="grid-list">
            {displayedFestivals.length === 0 ? <div className="grid-empty">&#44160;&#49353; &#44208;&#44284;&#44032; &#50630;&#49845;&#45768;&#45796;.</div> : displayedFestivals.map((f, idx) => {
              const tc = getTC(f.eventType); const isSelected = selectedKey === getFestivalKey(f); const hasCoord = f.mapy && f.mapx && !isNaN(parseFloat(f.mapy)); const isKopisGroup = isKopisGroupedItem(f);
              return <div key={getFestivalKey(f)} ref={el => { if (el) gridItemRefs.current[getFestivalKey(f)] = el; }} className={`grid-item${!hasCoord ? ' grid-item-nocoord' : ''}`} onClick={() => handleGridSelect(f)} style={{ background: isSelected ? `linear-gradient(135deg,${tc.light},white)` : 'white', borderColor: isSelected ? `${tc.color}50` : 'rgba(0,0,0,0.07)', boxShadow: isSelected ? `0 4px 20px ${tc.color}20` : '0 1px 4px rgba(0,0,0,0.04)', animation: `fadeUp 0.28s ${idx * 30}ms ease both` }}>
                {isSelected && <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: `linear-gradient(180deg,${tc.color},${tc.color}99)`, borderRadius: '12px 0 0 12px' }} />}
                <div className="grid-item-top"><div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{!hasCoord && <span className="no-coord-badge">&#50948;&#52824;&#50630;&#51020;</span>}<span className={`type-badge type-${f.eventType === 'EV01' ? 'festival' : f.eventType === 'EV02' ? 'performance' : f.eventType === 'EV03' ? 'event' : 'std'}`}>{tc.emoji} {tc.label}{isKopisGroup ? ` ${f.performanceCount || f.performances.length}\uAC1C` : ''}</span></div><span style={{ fontSize: 10.5, color: '#9ca3af' }}>{f.addr1 ? f.addr1.split(' ').slice(0, 2).join(' ') : ''}</span></div>
                <div className="grid-item-title" style={{ paddingLeft: isSelected ? 4 : 0 }}>{f.title}</div><div className="grid-item-date" style={{ paddingLeft: isSelected ? 4 : 0 }}><svg width="11" height="11" viewBox="0 0 11 11" fill="none"><rect x="1" y="2" width="9" height="8" rx="1.5" stroke="#9ca3af" strokeWidth="1"/><path d="M3.5 1v2M7.5 1v2M1 5h9" stroke="#9ca3af" strokeWidth="1" strokeLinecap="round"/></svg>{formatDate(f.eventStartDate)} ~ {formatDate(f.eventEndDate)}</div>
                {isKopisGroup && <div className="kopis-card-performances" style={{ paddingLeft: isSelected ? 4 : 0 }}>{f.performances.slice(0, 3).map(p => <div key={p.performanceId || p.title} className="kopis-card-performance"><span>{p.title}</span><em>{formatDate(p.startDate)} ~ {formatDate(p.endDate)}</em></div>)}{f.performances.length > 3 && <div className="kopis-card-more">&#50808; {f.performances.length - 3}&#44060; {groupedItemLabel(f)}</div>}</div>}
              </div>;
            })}
          </div>
        </aside>

        <aside className={`detail-panel ${detailData ? 'detail-open' : ''} ${detailCollapsed ? 'detail-collapsed' : ''}`} style={{ width: panelWidth }}>
          <div className="panel-resize-handle detail-resize-handle" onMouseDown={startPanelResize} title="resize panel" />
          {detailData && detailTC && (() => { const item = detailData; return <>
            <div className="detail-header-bar"
              onTouchStart={handleDetailTouchStart} onTouchMove={handleDetailTouchMove} onTouchEnd={handleDetailTouchEnd}>
              <button className="detail-back-btn" onClick={clearSelection} aria-label="close detail" title="close detail"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3.5 4L7.5 8L3.5 12" stroke="#374151" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><path d="M8.5 4L12.5 8L8.5 12" stroke="#374151" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg></button><div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><button className="detail-collapse-btn" onClick={() => setDetailCollapsed(!detailCollapsed)} aria-label="toggle detail height" title="toggle detail height">{detailCollapsed ? (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"></polyline></svg>) : (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>)}</button>{[{ lbl: '\u2039', disabled: selectedIdx <= 0, onClick: () => handleGridSelect(displayedFestivals[selectedIdx - 1]) }, { lbl: '\u203A', disabled: selectedIdx >= displayedFestivals.length - 1, onClick: () => handleGridSelect(displayedFestivals[selectedIdx + 1]) }].map(btn => <button key={btn.lbl} onClick={btn.onClick} disabled={btn.disabled} style={{ width: 28, height: 28, borderRadius: 7, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', background: btn.disabled ? 'rgba(0,0,0,0.04)' : `${detailTC.color}12`, border: `1px solid ${btn.disabled ? 'rgba(0,0,0,0.07)' : detailTC.color + '30'}`, color: btn.disabled ? '#d1d5db' : detailTC.color, cursor: btn.disabled ? 'not-allowed' : 'pointer' }}>{btn.lbl}</button>)}</div></div>
            <div className="detail-hero" style={{ background: `linear-gradient(160deg,${detailTC.light},white)` }}><div className="detail-hero-meta"><span className={`type-badge type-${item.eventType === 'EV01' ? 'festival' : item.eventType === 'EV02' ? 'performance' : item.eventType === 'EV03' ? 'event' : 'std'}`}>{detailTC.emoji} {detailTC.label}</span><span className="detail-hero-region">{item.addr1 ? item.addr1.split(' ').slice(0, 2).join(' ') : ''}</span></div><div className="detail-hero-title">{item.title}</div></div>
            <div className="detail-tabs">{[{ k: 'info', l: '\uC0C1\uC138\uC815\uBCF4' }, { k: 'nearby', l: '\uADFC\uCC98\uAD00\uAD11\uC9C0' }].map(t => <button key={t.k} className={`detail-tab ${detailTab === t.k ? 'detail-tab-active' : ''}`} onClick={() => { if (t.k === 'nearby') handleNearbyTab(nearbyRadius); else { suppressPopupCloseRef.current = true; suppressNearbyPopupCloseRef.current = true; setDetailTab('info'); if (mapRef.current) mapRef.current.closePopup(); setTimeout(() => { suppressNearbyPopupCloseRef.current = false; suppressPopupCloseRef.current = false; }, 150); } }} style={{ color: detailTab === t.k ? detailTC.color : '#9ca3af' }}>{t.l}</button>)}</div>
            <div className="detail-body">{detailTab === 'info' && <div className="detail-body-inner" style={{ animation: 'fadeIn 0.2s ease' }}>{item.firstimage && <div className="detail-image"><img src={item.firstimage} alt={item.title} /></div>}<table className="detail-table"><tbody>{item.overview && <tr><th>&#44060;&#50836;</th><td><CollapsibleText text={stripHtml(item.overview)} /></td></tr>}<tr><th>&#44592;&#44036;</th><td>{formatDate(item.eventStartDate)} ~ {formatDate(item.eventEndDate)}</td></tr>{item.sourceCategory && <tr><th>&#48516;&#47448;</th><td>{item.sourceCategory}</td></tr>}{item.addr1 && <tr><th>&#51452;&#49548;</th><td>{[item.addr1, item.addr2].filter(Boolean).join(' ')}</td></tr>}{item.eventplace && <tr><th>&#51109;&#49548;</th><td>{item.eventplace}</td></tr>}{item.tel && <tr><th>&#51204;&#54868;</th><td>{item.tel}</td></tr>}{item.sponsor1 && <tr><th>&#51452;&#52572;</th><td>{item.sponsor1}{item.sponsor1tel ? ` (${item.sponsor1tel})` : ''}</td></tr>}{item.sponsor2 && <tr><th>&#51452;&#44288;</th><td>{item.sponsor2}{item.sponsor2tel ? ` (${item.sponsor2tel})` : ''}</td></tr>}{item.playtime && <tr><th>&#49884;&#44036;</th><td>{item.playtime}</td></tr>}{item.useTarget && !item.agelimit && <tr><th>&#45824;&#49345;</th><td>{item.useTarget}</td></tr>}{item.agelimit && <tr><th>&#50672;&#47161;</th><td>{item.agelimit}</td></tr>}{item.usetimefestival && <tr><th>&#50836;&#44552;</th><td>{item.usetimefestival}</td></tr>}{item.player && <tr><th>&#52636;&#50672;</th><td>{item.player}</td></tr>}{item.discountinfofestival && <tr><th>&#54624;&#51064;</th><td>{item.discountinfofestival}</td></tr>}{item.spendtimefestival && <tr><th>&#49548;&#50836;&#49884;&#44036;</th><td>{item.spendtimefestival}</td></tr>}{item.bookingplace && <tr><th>&#50696;&#47588;&#52376;</th><td>{item.bookingplace}</td></tr>}{item.festivalgrade && <tr><th>&#46321;&#44553;</th><td>{item.festivalgrade}</td></tr>}{item.program && <tr><th>&#54532;&#47196;&#44536;&#47016;</th><td><CollapsibleText text={stripHtml(item.program)} /></td></tr>}{item.subevent && <tr><th>&#48512;&#45824;&#54665;&#49324;</th><td><CollapsibleText text={stripHtml(item.subevent)} /></td></tr>}{item.placeinfo && <tr><th>&#51109;&#49548;&#50504;&#45236;</th><td><CollapsibleText text={stripHtml(item.placeinfo)} /></td></tr>}{item.etcDesc && !item.overview && <tr><th>&#44592;&#53440;</th><td><CollapsibleText text={stripHtml(item.etcDesc)} /></td></tr>}{item.eventhomepage && <tr><th>&#54856;&#54168;&#51060;&#51648;</th><td style={{ wordBreak: 'break-all' }}>{renderLink(item.eventhomepage)}</td></tr>}{item.homepage && !item.eventhomepage && <tr><th>&#54856;&#54168;&#51060;&#51648;</th><td style={{ wordBreak: 'break-all' }}>{renderLink(item.homepage)}</td></tr>}{item.detailInfos && item.detailInfos.filter(info => info.infoName && info.infoText).map(info => <tr key={`${info.serialNum || ''}-${info.infoName}`}><th>{info.infoName}</th><td><CollapsibleText text={stripHtml(info.infoText)} /></td></tr>)}</tbody></table>{selectedKey === loadingDetailKey && <div className="detail-loading-section"><div className="detail-spinner" style={{ borderColor: `${detailTC.color}33`, borderTopColor: detailTC.color }}></div><span className="detail-loading-text">상세 정보 조회 중...</span></div>}{isKopisGroupedItem(item) && item.performances.length > 0 && <div className="kopis-detail-performances"><div className="kopis-detail-title">{groupedItemLabel(item)} &#47785;&#47197; {item.performances.length}&#44060;</div>{item.performances.map(p => <div key={p.performanceId || p.title} className="kopis-detail-performance">{p.poster && <img src={p.poster} alt={p.title} />}<div><strong>{p.title}</strong><span>{formatDate(p.startDate)} ~ {formatDate(p.endDate)}{p.state ? ` · ${p.state}` : ''}</span>{p.genre && <span>{p.genre}</span>}{p.runtime && <span>{groupedItemLabel(item)}&#49884;&#44036; {p.runtime}</span>}{p.age && <span>&#44288;&#46988;&#50672;&#47161; {p.age}</span>}{p.price && <span>{stripHtml(p.price)}</span>}{p.schedule && <span>{stripHtml(p.schedule)}</span>}{p.description && <CollapsibleText text={stripHtml(p.description)} />}{p.relateUrl && <span>{renderLink(p.relateUrl)}</span>}</div></div>)}</div>}{(item.sourceName || item.sourceUrl || item.apiSource || item.sourceCategory) && <div className="detail-source-section"><SourceAttribution item={item} /></div>}</div>}
            {detailTab === 'nearby' && <><div className="nearby-options">{[5, 10].map(r => <button key={r} className="nearby-radius-btn" onClick={() => handleNearbyTab(r)} style={{ background: nearbyRadius === r ? detailTC.color : 'transparent', color: nearbyRadius === r ? 'white' : detailTC.color, borderColor: `${detailTC.color}50` }}>{r}km</button>)}<span className="nearby-count">{nearbyAttractions.length}&#44060; &#44288;&#44305;&#51648;</span></div><div style={{ animation: 'fadeIn 0.2s ease' }}>{nearbyAttractions.length === 0 ? <div className="grid-empty">&#44540;&#52376; &#44288;&#44305;&#51648;&#44032; &#50630;&#49845;&#45768;&#45796;.</div> : nearbyAttractions.map(a => { const isExp = expandedAttrId === a.id; const isSelectedNearby = selectedNearbyId === a.id; return <div key={a.id} ref={el => { if (el) nearbyItemRefs.current[a.id] = el; }} className={`nearby-item ${isExp ? 'nearby-item-expanded' : ''} ${isSelectedNearby ? 'nearby-item-selected' : ''}`}><div className="nearby-item-header" onClick={() => handleNearbySelect(a)}><span className="nearby-item-name">{a.title}</span><span className={`nearby-arrow ${isExp ? 'nearby-arrow-up' : ''}`}>&#9660;</span></div>{isExp && <div className="nearby-item-detail">{a.firstimage2 && <div className="detail-image detail-image-small"><img src={a.firstimage2} alt={a.title} /></div>}<table className="detail-table"><tbody>{a.addr1 && <tr><th>&#51452;&#49548;</th><td>{a.addr1}</td></tr>}{a.tel && <tr><th>&#51204;&#54868;</th><td>{a.tel}</td></tr>}{a.infocenter && <tr><th>&#47928;&#51032;</th><td>{a.infocenter}</td></tr>}{a.usetime && <tr><th>&#51060;&#50857;&#49884;&#44036;</th><td><CollapsibleText text={stripHtml(a.usetime)} /></td></tr>}{a.restdate && <tr><th>&#49772;&#45716;&#45216;</th><td>{a.restdate}</td></tr>}{a.parking && <tr><th>&#51452;&#52264;</th><td><CollapsibleText text={stripHtml(a.parking)} /></td></tr>}{a.chkpet && <tr><th>&#48152;&#47140;&#46041;&#47932;</th><td>{a.chkpet}</td></tr>}{a.overview && <tr><th>&#49548;&#44060;</th><td><CollapsibleText text={stripHtml(a.overview)} /></td></tr>}{(nearbyDetailInfos[a.contentId] || []).filter(info => info.infoName && info.infoText).map(info => <tr key={`${info.serialNum || ''}-${info.infoName}`}><th>{info.infoName}</th><td><CollapsibleText text={stripHtml(info.infoText)} /></td></tr>)}</tbody></table>{loadingNearbyId === a.contentId && <div className="detail-loading-section" style={{ padding: '20px 10px', margin: '10px 0', gap: '8px' }}><div className="detail-spinner" style={{ width: '20px', height: '20px', borderWidth: '2.5px', borderColor: `${detailTC.color}33`, borderTopColor: detailTC.color }}></div><span className="detail-loading-text" style={{ fontSize: '11.5px' }}>관광지 상세 정보 조회 중...</span></div>}</div>}</div>; })}</div></>}
            </div>
          </>; })()}
        </aside>
      </div>
      <footer className="footer"><span className="footer-ad-label">AD</span>{[{ w: '40%', label: '728x90' }, { w: '20%', label: '300x50' }, { w: '15%', label: '250x50' }].map((b, i) => <div key={i} className="footer-ad-block" style={{ flex: `0 0 ${b.w}` }}><span className="footer-ad-text">&#44305;&#44256; {b.label}</span></div>)}<nav className="footer-links"><a href="/festivals">&#52629;&#51228;&#47785;&#47197;</a><a href="/privacy">&#44060;&#51064;&#51221;&#48372;&#52376;&#47532;&#48169;&#52840;</a><a href="/contact">&#47928;&#51032;</a><a href="/sources">&#45936;&#51060;&#53552; &#52636;&#52376;</a></nav></footer>
    </div>
    {process.env.NODE_ENV === 'development' && <Agentation />}
    </>
  );
}

